import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Card,
  Divider,
  makeStyles,
  MenuItem,
  Typography
} from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Formik, Form } from 'formik'
import ViewHeader from 'components/ViewHeader'
import ProgressButton from 'components/ProgressButton'
import { addSponsor, fetchSponsorById, resetSponsor, updateSponsorById } from 'store/sponsor'
import ActionBar from 'components/ActionBar'
import CustomTextField from 'components/Form/CustomTextField'
import { Collection, Roles, SPONSOR_INFO_TEXT } from 'constants/index'
import { sponsorFormSchema } from 'schemas/sponsor'
import { getSponsorshipLevels } from 'store/sponsorship-level'
import { submitSubjectData } from 'store/subject'
import { omit } from 'lodash'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'
import TextField from '@material-ui/core/TextField'
import { Add } from '@material-ui/icons'
import { colors } from 'utils/colors'
import CustomPopup from 'components/CustomPopup'
import SponsorshipLevelsForm from 'views/admin/sponsorshipLevels/SponsorshipLevelsForm'
import InfoIconAlongText from 'components/InfoIconAlongText'
import ReactQuill from 'react-quill'
import { modules } from 'views/admin/settings/SettingForm/TemplateForm'
import ErrorMessage from 'components/ErrorMessage'

const SponsorImageKeys = {
  logoImage: 'logoImageName',
  adImage: 'adImageName',
  brochureImage: 'brochureImageName'
}

const SponsorForm = ({ displayContext, onSuccess, onCloseModal }) => {
  const classes = useStyles()
  const { sponsorId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const [openSponsorShipLevelModal, setOpenSponsorShipLevelModal] = useState(false)

  const { user } = useSelector((state) => state.auth)
  const { sponsor } = useSelector((state) => state.sponsor)
  const { sponsorshipLevels, inProgress: sponsorshipLevelProgress } = useSelector(
    (state) => state.sponsorshipLevel
  )

  const initialValues = {
    fullName: sponsor?.fullName ?? '',
    email: sponsor?.email ?? '',
    intro: sponsor?.intro ?? '',
    websiteUrl: sponsor?.websiteUrl ?? '',
    adImageName: sponsor?.adImageName ?? null,
    logoImageName: sponsor?.logoImageName ?? null,
    brochureImageName: sponsor?.brochureImageName ?? null,
    sponsorMessage: sponsor?.sponsorMessage ?? '',
    sponsorshipLevelId: sponsor?.sponsorshipLevelId ?? '',
    sponsorAdTargetURL: sponsor?.sponsorAdTargetURL ?? '',
    booth: sponsor?.booth ?? ''
  }

  const [adImageFile, setAdImageFile] = useState(null)
  const [logoImageFile, setLogoImageFile] = useState(null)
  const [brochureImageFile, setBrochureImageFile] = useState(null)
  const [loading, setLoading] = useState(false)

  const goBack = () => {
    dispatch(resetSponsor())
    history.goBack()
  }

  useEffect(() => {
    if (sponsorId) {
      dispatch(fetchSponsorById(sponsorId))
    }
    return () => dispatch(resetSponsor())
  }, [dispatch, sponsorId])

  useEffect(() => {
    dispatch(getSponsorshipLevels(user?.conferenceId))
  }, [dispatch, user?.conferenceId])

  const onCancel = () => {
    if (displayContext === 'page') {
      goBack()
    } else {
      onCloseModal()
    }
  }

  const handleFilesChange = (key) => (file) => {
    if (key === SponsorImageKeys.logoImage) {
      setLogoImageFile(file)
    } else if (key === SponsorImageKeys.adImage) {
      setAdImageFile(file)
    } else {
      setBrochureImageFile(file)
    }
  }

  const handleUpload = (key) => async (name) => {
    const modifiedSponsor = omit(sponsor, 'id')
    if (key === SponsorImageKeys.logoImage) {
      modifiedSponsor[SponsorImageKeys.logoImage] = name
    } else if (key === SponsorImageKeys.adImage) {
      modifiedSponsor[SponsorImageKeys.adImage] = name
    } else modifiedSponsor[SponsorImageKeys.brochureImage] = name
    await dispatch(updateSponsorById(sponsorId, modifiedSponsor, user?.conferenceId))
  }

  const handleDelete = (key) => async () => {
    const modifiedSponsor = omit(sponsor, 'id')
    if (key === SponsorImageKeys.logoImage) {
      modifiedSponsor[SponsorImageKeys.logoImage] = null
    } else if (key === SponsorImageKeys.adImage) {
      modifiedSponsor[SponsorImageKeys.adImage] = null
    } else modifiedSponsor[SponsorImageKeys.brochureImage] = null
    await dispatch(updateSponsorById(sponsorId, modifiedSponsor, user?.conferenceId))
  }

  const onSubmit = async (values) => {
    setLoading(true)
    const payload = {
      fullName: values.fullName.trim(),
      email: values.email.trim(),
      intro: values.intro.trim(),
      websiteUrl: values.websiteUrl.trim(),
      sponsorMessage: values.sponsorMessage.trim(),
      sponsorshipLevelId: values.sponsorshipLevelId.trim(),
      sponsorAdTargetURL: values.sponsorAdTargetURL.trim(),
      booth: values?.booth ? values?.booth.trim() : ''
    }
    let res, message
    if (sponsorId) {
      res = await dispatch(updateSponsorById(sponsorId, payload, user?.conferenceId))
      sponsor?.subjectId && (await dispatch(submitSubjectData(sponsor?.subjectId, payload)))
      message = 'Sponsor updated successfully.'
    } else {
      if (logoImageFile) {
        payload.logoImageName = await uploadImageFirstTime(
          logoImageFile,
          Collection.SPONSORS,
          user?.conferenceId
        )
      }
      if (adImageFile) {
        payload.adImageName = await uploadImageFirstTime(
          adImageFile,
          Collection.SPONSORS,
          user?.conferenceId
        )
      }
      if (brochureImageFile) {
        payload.brochureImageName = await uploadImageFirstTime(
          brochureImageFile,
          Collection.SPONSORS,
          user?.conferenceId
        )
      }
      res = await dispatch(addSponsor(payload, user?.conferenceId))
      message = 'Sponsor added successfully.'
    }
    setLoading(false)
    if (res && displayContext === 'page') {
      toast.success(message)
      goBack()
    }
    displayContext === 'modal' && onSuccess && onSuccess(res)
  }

  if (sponsorshipLevelProgress) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: '5rem' }}>
        <CircularProgress />
      </Box>
    )
  }
  return (
    <Card
      elevation={displayContext === 'page' ? 3 : 0}
      className={displayContext === 'page' ? classes.card : classes.styledCard}>
      {displayContext === 'page' && (
        <ViewHeader title={sponsorId ? 'Edit sponsor' : 'Create sponsor'}>
          <ActionBar actionText="Back" onAction={goBack} />
        </ViewHeader>
      )}
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={sponsorFormSchema({ showSponsorshipLevel: true })}
        onSubmit={onSubmit}>
        {({ values, setFieldValue, touched, errors }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
                <CustomTextField label="Company" name="fullName" type="text" />
              </Grid>
              <Grid item xs={12} lg={4}>
                <TextField
                  select
                  fullWidth
                  label="Sponsorship Level"
                  variant="outlined"
                  error={Boolean(touched.sponsorshipLevelId && errors.sponsorshipLevelId)}
                  value={values.sponsorshipLevelId}
                  onChange={(event) =>
                    event.target.value && setFieldValue('sponsorshipLevelId', event.target.value)
                  }
                  helperText={
                    touched.sponsorshipLevelId && errors.sponsorshipLevelId
                      ? errors.sponsorshipLevelId
                      : null
                  }>
                  {sponsorshipLevels?.map((level) => (
                    <MenuItem value={level.id} key={level.id}>
                      <Typography>{level.category}</Typography>
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem onClick={() => setOpenSponsorShipLevelModal(true)}>
                    <Typography className={classes.buttonItem}>
                      <Add />
                      Create sponsorship level
                    </Typography>
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <CustomTextField label="Booth No" name="booth" type="text" />
              </Grid>
              <Grid item xs={12} lg={6}>
                <CustomTextField label="Email" name="email" type="email" disabled={!!sponsorId} />
              </Grid>
              <Grid item xs={12} lg={6}>
                <CustomTextField label="Website URL" name="websiteUrl" type="text" />
              </Grid>
              <Grid item xs={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.label}>
                    Introduction
                    <InfoIconAlongText infoText={SPONSOR_INFO_TEXT.introduction} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.intro}
                    onChange={(value) => setFieldValue('intro', value)}
                    placeholder="Write introduction here..."
                  />
                  <ErrorMessage message={touched?.intro && errors?.intro} />
                </Box>
              </Grid>
              <Grid item xs={12} lg={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <label className={classes.label}>
                    Message
                    <InfoIconAlongText infoText={SPONSOR_INFO_TEXT.message} />
                  </label>
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    value={values.sponsorMessage}
                    onChange={(value) => setFieldValue('sponsorMessage', value)}
                    placeholder="Write message here..."
                  />
                  <ErrorMessage message={touched?.sponsorMessage && errors?.sponsorMessage} />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  label="Banner Click URL"
                  name="sponsorAdTargetURL"
                  type="text"
                  infoText={SPONSOR_INFO_TEXT.bannerClickURL}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Sponsor Logo"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x320"
                  path={Collection.SPONSORS}
                  handleCustomChange={(file) => handleFilesChange(SponsorImageKeys.logoImage)(file)}
                  onUpload={(name) => handleUpload(SponsorImageKeys.logoImage)(name)}
                  onDelete={() => handleDelete(SponsorImageKeys.logoImage)()}
                  imageName={sponsorId ? sponsor?.logoImageName : null}
                  resetImage={!(sponsorId && sponsor?.logoImageName)}
                  conferenceId={user?.role === Roles.ADMIN ? user?.conferenceId : ''}
                  infoText={SPONSOR_INFO_TEXT.logo}
                  firsTimeUpload={!sponsorId}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Sponsor Ad Banner"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x50"
                  path={Collection.SPONSORS}
                  handleCustomChange={(file) => handleFilesChange(SponsorImageKeys.adImage)(file)}
                  onUpload={(name) => handleUpload(SponsorImageKeys.adImage)(name)}
                  onDelete={() => handleDelete(SponsorImageKeys.adImage)()}
                  imageName={sponsorId ? sponsor?.adImageName : null}
                  resetImage={!(sponsorId && sponsor?.adImageName)}
                  conferenceId={user?.conferenceId}
                  infoText={SPONSOR_INFO_TEXT.adBanner}
                  firsTimeUpload={!sponsorId}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Sponsor Brochure"
                  subTitle="Allowed Format: png/jpg ; Dimensions : 320x50"
                  path={Collection.SPONSORS}
                  handleCustomChange={(file) =>
                    handleFilesChange(SponsorImageKeys.brochureImage)(file)
                  }
                  onUpload={(name) => handleUpload(SponsorImageKeys.brochureImage)(name)}
                  onDelete={() => handleDelete(SponsorImageKeys.brochureImage)()}
                  imageName={sponsorId ? sponsor?.brochureImageName : null}
                  resetImage={!(sponsorId && sponsor?.brochureImageName)}
                  conferenceId={user?.role === Roles.ADMIN ? user?.conferenceId : ''}
                  infoText={SPONSOR_INFO_TEXT.brochure}
                  firsTimeUpload={!sponsorId}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" spacing={3}>
              <Grid item>
                <Button size="large" disabled={loading} onClick={onCancel}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <ProgressButton
                  type="submit"
                  label="Save"
                  inProgress={loading}
                  disabled={loading}
                />
              </Grid>
            </Grid>

            <CustomPopup
              title="Create sponsorship level"
              maxWidth="md"
              open={openSponsorShipLevelModal}
              onClose={() => setOpenSponsorShipLevelModal(false)}>
              <SponsorshipLevelsForm
                displayContext="modal"
                onCloseModal={() => setOpenSponsorShipLevelModal(false)}
                onSuccess={(sponsorshipLevelId) => {
                  setOpenSponsorShipLevelModal(false)
                  setFieldValue('sponsorshipLevelId', sponsorshipLevelId)
                }}
              />
            </CustomPopup>
          </Form>
        )}
      </Formik>
    </Card>
  )
}
export default SponsorForm

const useStyles = makeStyles({
  card: {
    padding: '24px',
    overflow: 'visible'
  },
  buttonItem: {
    color: colors.indigo,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem'
  },
  styledCard: {
    paddingTop: '1rem'
  },
  label: {
    fontSize: '13px',
    letterSpacing: '0.5px',
    color: '#606060',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginLeft: '0.2rem'
  }
})
